<script lang="ts">
	import imgDevRoom from "@assets/images/about/dev-room.jpg?webp&h=602&imagetools";
	import imgStudent from "@assets/images/about/student-dev.jpg?webp&w=843&imagetools";
	import imgTesting from "@assets/images/about/testing.jpg?webp&w=843&imagetools";
	import imgCompanyLunch from "@assets/images/career/company-lunch.jpg?webp&w=843&imagetools";
	import imgDavidOnCall from "@assets/images/career/david-on-call.jpg?webp&h=602&imagetools";
	import imgMountains from "@assets/images/career/mountains.jpg?webp&h=602&imagetools";
	import imgNelaRichardCoop from "@assets/images/career/nela-richard-coop.jpg?webp&h=602&imagetools";
	import imgPaintBallCrew from "@assets/images/career/paintball-crew-2023.jpg?webp&h=602&imagetools";
	import imgPubFootball from "@assets/images/career/pub-football.jpg?webp&h=602&imagetools";
	import imgSpeakerJacob from "@assets/images/career/speaker-jacob.jpg?webp&h=602&imagetools";
	import imgTomasRichardFocusing from "@assets/images/career/tomas-richard-focusing.jpg?webp&h=602&imagetools";
	import imgTyson007 from "@assets/images/career/tyson-007.jpg?webp&h=602&imagetools";
	import { Background } from "@components/Background.js";
	import CareerFormReversedLayout from "@components/CareerFormReversedLayout.svelte";
	import Photogallery from "@components/Photogallery.svelte";
	import Pill from "@components/Pill.svelte";
	import { PillColor, PillSize } from "@components/PillProps.js";
	import Section from "@components/Section.svelte";
	import Ul from "@components/Ul.svelte";
	import { UlSize } from "@components/UlProps.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import type { MediaSrc } from "@components/common/MediaSrc.js";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { Locale } from "@core/schema/Locale.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import type { CareerPositionDetailProps } from "./CareerPositionDetailProps.js";

	const { position }: CareerPositionDetailProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const anchorApplication = "prihlaska";

	const texts = {
		apply: {
			[Locale.cs]: "Vyplnit přihlášku",
			[Locale.en]: "Fill in the application",
		}[tenant.locale],
		bannerHeadline: {
			[Locale.cs]: "Sedí ti tahle pozice? nl[[]] cw[[Pojďme to probrat]]",
			[Locale.en]: "Is this role for you? nl[[]] cw[[Let’s talk it through]]",
		}[tenant.locale],
		bannerLead: {
			[Locale.cs]: "Nepotřebujeme životopis na tři strany ani pohovor na pět kol.",
			[Locale.en]: "No three-page CV, no five-round interview.",
		}[tenant.locale],
		bannerDesc: {
			[Locale.cs]: "Napiš nám pár řádků o sobě a domluvíme si call nebo kávu.",
			[Locale.en]: "Send us a few lines about yourself and we’ll set up a call or a coffee.",
		}[tenant.locale],
		formHeadline: {
			[Locale.cs]: "Nech kontakt a přivítáme tě cg[[u nás na kávičce]]",
			[Locale.en]: "Leave us your details and we’ll welcome you cg[[over a coffee]]",
		}[tenant.locale],
	};

	const images: MediaSrc[] = [
		{
			src: imgDevRoom,
			type: "image/webp",
		},
		{
			src: imgDavidOnCall,
			type: "image/webp",
		},
		{
			src: imgTomasRichardFocusing,
			type: "image/webp",
		},
		{
			src: imgTesting,
			type: "image/webp",
		},
		{
			src: imgMountains,
			type: "image/webp",
		},
		{
			src: imgPaintBallCrew,
			type: "image/webp",
		},
		{
			src: imgPubFootball,
			type: "image/webp",
		},

		{
			src: imgCompanyLunch,
			type: "image/webp",
		},
		{
			src: imgSpeakerJacob,
			type: "image/webp",
		},
		{
			src: imgTyson007,
			type: "image/webp",
		},
		{
			src: imgStudent,
			type: "image/webp",
		},
		{
			src: imgNelaRichardCoop,
			type: "image/webp",
		},
	];

	const requirementsItems = position.requirements.map((item) => ({
		title: item.title,
		descItems: (item.descItems ?? []).map((text) => text),
		customNumber: item.customNumber,
	}));
</script>

<Layout>
	<Section
		hero
		background={Background.LightGradient}
		gridChildren="container-12 md:container-10 xl:container-start-2 xl:container-end-11"
	>
		<div class="flex flex-col gap-6 lg:max-w-[46rem]">
			<h1 class="text-fstonyx">
				<Formatted text={position.perex} />
			</h1>

			<p class="mb-0 text-base text-fstgrey-600">
				<Formatted text={position.introduction} />
			</p>

			<div class="flex flex-wrap gap-2">
				{#if position.badges?.length}
					{#each position.badges ?? [] as badge, badgeIndex (badgeIndex)}
						<Pill color={PillColor.White} size={PillSize.Small}>
							<Formatted text={badge.text} />
						</Pill>
					{/each}
				{/if}

				{#if position.stackBadges?.length}
					{#each position.stackBadges ?? [] as stackBadge (stackBadge)}
						<Pill color={PillColor.White} size={PillSize.Small}>
							<Formatted text={stackBadge} />
						</Pill>
					{/each}
				{/if}
			</div>

			<Link
				variant={ButtonVariant.Phlox}
				class="mt-2 flex w-fit items-center justify-center"
				href="#{anchorApplication}"
			>
				{texts.apply}
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	<Section gridChildren="container-12">
		<div class="grid items-start gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
			{#each requirementsItems as requirementsItem, requirementsIndex (requirementsItem.title)}
				{@const isWhatWeOffer = requirementsIndex === requirementsItems.length - 1}
				<div
					class={[
						"rounded-2xl bg-white p-6 sm:p-8",
						// the closing block is what the candidate gets rather than what they bring, so it carries the accent
						isWhatWeOffer && "border-t-4 border-fstgreen",
					]}
				>
					<Ul items={[requirementsItem]} size={UlSize.Medium} largeTitle />
				</div>
			{/each}
		</div>
	</Section>

	<Photogallery class="full-width mb-14 md:mb-20" {images} />

	<section class="bg-fstphlox full-width grid-container py-14 md:pb-28 md:pt-24">
		<h2 class="mx-auto max-w-2xl text-center xl:max-w-3xl">
			<Formatted text={texts.bannerHeadline} />
		</h2>

		<p class="text-md mx-auto mb-1 mt-10 max-w-2xl text-center font-medium text-fstonyx xl:max-w-4xl">
			<Formatted text={texts.bannerLead} />
		</p>
		<p class="text-md mx-auto max-w-3xl text-center text-fstonyx">
			<Formatted text={texts.bannerDesc} />
		</p>

		<div class="max-sm:grid-container">
			<Link variant={ButtonVariant.Green} class="mx-auto flex items-center justify-center" href="#{anchorApplication}">
				{texts.apply}
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</section>

	<Section
		id={anchorApplication}
		gridChildren="container-12 md:container-10 xl:container-start-2 xl:container-end-11 text-fstonyx"
	>
		<CareerFormReversedLayout {position} headlineClass="leading-[1.2] md:leading-[1]">
			{#snippet headline()}
				<Formatted text={texts.formHeadline} />
			{/snippet}
		</CareerFormReversedLayout>
	</Section>
</Layout>
